import { useState, useMemo } from "react";
import { externalLinks, linkCategories } from "@/mocks/resourcesData";
import Icon from "@/components/feature/Icon";
import { usePublicRows } from "@/hooks/usePublicContent";
import type { DbExternalLink } from "@/types/database";

type CategoryKey = string;

// Built-in fallback used until the `external_links` table exists.
const FALLBACK: DbExternalLink[] = externalLinks.map((l, i) => ({
  id: -(i + 1),
  title: l.title,
  description: l.description,
  url: l.url,
  category: l.category,
  icon: l.icon,
  sort_order: i + 1,
  is_active: true,
  created_at: "",
  updated_at: "",
}));

export default function ExternalLinksSection() {
  const [activeCategory, setActiveCategory] = useState<CategoryKey>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const { rows: links } = usePublicRows<DbExternalLink>("external_links", FALLBACK);

  // Tabs: the known categories first (in their usual order), then any new
  // category the association added in the backend.
  const categories = useMemo(() => {
    const present = new Set(links.map((l) => l.category));
    const known = linkCategories.filter((c) => c.key === "all" || present.has(c.key));
    const extra = Array.from(present)
      .filter((c) => !linkCategories.some((k) => k.key === c))
      .map((c) => ({ key: c, label: c }));
    return [...known, ...extra];
  }, [links]);

  const filtered = useMemo(() => {
    return links.filter((l) => {
      const matchCategory = activeCategory === "all" || l.category === activeCategory;
      const matchSearch =
        !searchQuery ||
        l.title.includes(searchQuery) ||
        (l.description ?? "").includes(searchQuery);
      return matchCategory && matchSearch;
    });
  }, [links, activeCategory, searchQuery]);

  const activeClass =
    "bg-navy-950 text-white shadow-lg shadow-navy-950/30 ring-2 ring-white/20";
  const inactiveClass =
    "bg-white text-silver-600 hover:bg-silver-50 border border-silver-200";

  return (
    <section id="links" className="py-24 md:py-32 bg-silver-50">
      <div className="px-6 md:px-10 lg:px-16">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
          <div>
            <p className="text-sm font-medium tracking-[0.2em] text-navy-700 uppercase mb-3">
              Related Links
            </p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-semibold text-navy-950 leading-tight">
              相關法規與實用連結
            </h2>
          </div>

          {/* Search */}
          <div className="relative w-full md:w-80">
            <Icon className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-silver-400" />
            <input
              type="text"
              placeholder="搜尋連結..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-3 bg-white border border-silver-200 rounded-full text-sm text-navy-950 placeholder:text-silver-400 focus:outline-none focus:border-navy-700 transition-colors"
            />
          </div>
        </div>

        {/* Category filter tabs */}
        <div className="flex flex-wrap gap-2 mb-12 md:mb-16">
          {categories.map((cat) => (
            <button
              key={cat.key}
              onClick={() => setActiveCategory(cat.key)}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                activeCategory === cat.key ? activeClass : inactiveClass
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Flat grid - no grouping */}
        {filtered.length > 0 ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {filtered.map((link) => (
              <a
                key={link.id}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white rounded-3xl p-6 md:p-7 border border-silver-200 hover:border-navy-200 hover:shadow-lg transition-all duration-500 flex flex-col"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-11 h-11 rounded-2xl bg-navy-950 flex items-center justify-center text-white text-lg shrink-0">
                    <Icon className={link.icon || "ri-link-m"} />
                  </div>
                  <Icon className="ri-external-link-line text-silver-300 group-hover:text-navy-700 transition-colors" />
                </div>

                <h4 className="text-base font-semibold text-navy-950 mb-2 group-hover:text-navy-700 transition-colors">
                  {link.title}
                </h4>
                <p className="text-sm text-silver-500 leading-relaxed flex-grow">
                  {link.description}
                </p>

                <div className="mt-4 pt-4 border-t border-silver-100 flex items-center gap-2">
                  <span className="text-xs text-silver-400 bg-silver-50 px-2.5 py-1 rounded-full">
                    {link.category}
                  </span>
                </div>
              </a>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <Icon className="ri-link-m text-5xl text-silver-300 mb-4" />
            <p className="text-silver-500 text-lg mb-2">沒有符合條件的連結</p>
            <p className="text-silver-400 text-sm">請嘗試其他篩選條件或搜尋關鍵字</p>
          </div>
        )}
      </div>
    </section>
  );
}
